import { fetchWithTimeout, readJsonResponse, responseError, discardResponse } from './apiRequest';
import { retryRead } from './retryRead.mjs';
import { trackEvent } from './analytics';

export const HISTORY_PAGE_SIZE = 50;

export async function requestHistoryPage(base, session, options = {}, signal, read = fetchWithTimeout) {
  return retryRead(timeoutMs => requestHistoryPageOnce(base, session, options, signal, timeoutMs, read), {
    signal, onRecovered: measurements => trackEvent('api_read_recovered', { operation: 'history', ...measurements }),
  });
}

async function requestHistoryPageOnce(base, session, options, signal, timeoutMs, read) {
  const purpose = options.records ? 'history_delta' : options.purpose || (options.cursor ? 'history_page' : options.search ? 'history_search' : 'history_head');
  const query={...options};delete query.purpose;
  const response = await read(`${base}/history/query`, {
    method: 'POST', signal,requestPurpose:purpose,
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: HISTORY_PAGE_SIZE, ...query }),
  }, Math.min(15000, timeoutMs));
  let payload;
  if ([404, 405].includes(response.status)) {
    discardResponse(response);
    const legacy = await read(`${base}/history`, {
      signal, requestPurpose:purpose,headers: { Authorization: `Bearer ${session.access_token}` },
    }, Math.min(15000, timeoutMs));
    payload = await readJsonResponse(legacy);
    if (!legacy.ok) throw responseError(legacy, payload, 'History unavailable');
  } else {
    payload = await readJsonResponse(response);
    if (!response.ok) throw responseError(response, payload, 'History unavailable');
  }
  // Older deployments return the complete array. It remains usable during a
  // rolling release without sending private search text in an access-log URL.
  if (Array.isArray(payload)) payload = { items: payload, active: [], next_cursor: null };
  if (!payload || !Array.isArray(payload.items) || !Array.isArray(payload.active)
      || (payload.next_cursor !== null && typeof payload.next_cursor !== 'string')) {
    throw Object.assign(new Error('Invalid history response'), { code: 'malformed_response', operation: 'history', termination_reason: 'parse' });
  }
  if (options.records) payload.items = payload.items.filter(file => options.records.includes(file.id));
  return payload;
}

export function mergeHistory(previous, incoming, removed = []) {
  const deleted = new Set(removed);
  const records = new Map(previous.filter(file => !deleted.has(file.id)).map(file => [file.id, file]));
  incoming.forEach(file => records.set(file.id, file));
  return [...records.values()].sort((a, b) => String(b.uploaded_time || '').localeCompare(String(a.uploaded_time || ''))
    || String(b.id).localeCompare(String(a.id)));
}

export async function changedHistory(base, session, records, signal) {
  let items = [];
  for (let index = 0; index < records.length; index += HISTORY_PAGE_SIZE) {
    const page = await requestHistoryPage(base, session, { records: records.slice(index, index + HISTORY_PAGE_SIZE) }, signal);
    items = mergeHistory(items, page.items);
  }
  return items;
}

export async function allCompletedHistory(base, session, signal, read = fetchWithTimeout) {
  let cursor = null, files = [];
  const visited = new Set();
  do {
    const page = await requestHistoryPage(base, session, { filter: 'completed', cursor,purpose:'history_export' }, signal, read);
    files = mergeHistory(files, page.items);
    cursor = page.next_cursor;
    if (cursor && visited.has(cursor)) throw new Error('History pagination did not advance');
    visited.add(cursor);
  } while (cursor);
  return files.filter(file => String(file.status || '').trim().toLowerCase().startsWith('completed') && file.result_state !== 'empty');
}
